<template>
	<CloFixedPanels id="edit-report">
		<template v-slot:header>
			<!-- Edit header (shared across all create/edit views) -->
			<header id="edit-report-header" class="Page-section Page-section--primaryDark">
				<div class="Page-section-inner Page-section-inner--md">
					<!-- Title when report exists -->
					<ReportTitle
						v-if="tripReportModelData"
						:tripId="tripReportModelData.tripReportId"
						:tripName="tripReportModelData.name"
						:tripBeginDt="tripReportModelData.beginDt"
						:tripEndDt="tripReportModelData.endDt"
						:privacySetting="tripReportModelData.privacy"
						:locale="locale"
						:mode="mode"
						:view="view"
						:inline="true"
						:reverse="true"
					/>
					<!-- Title when report doesn't exist -->
					<ReportTitle v-else :mode="mode" :view="view" :locale="locale" :reverse="true" />
				</div>
			</header>
		</template>

		<template v-slot:mainContent>
			<!-- Definition -->
			<EditReportDefinition
				v-if="view === 'definition'"
				:locale="locale"
				:mode="mode"
				ref="editReportDef"
				:reportName="tripReportModelData.name"
				:beginDt="tripReportModelData.beginDt"
				:endDt="tripReportModelData.endDt"
				:privacy="tripReportModelData.privacy"
				:reportId="tripReportModelData.tripReportId"
				@setAreFieldsFilled="advanceStep"
			/>
			<!-- Manage checklists -->
			<EditReportChecklists
				v-if="view === 'checklists'"
				:locale="locale"
				:currentUser="currentUser"
				:mode="mode"
				ref="editChecklists"
				:reportId="tripReportModelData.tripReportId"
			/>
			<!-- Add people -->
			<EditReportPeople
				v-if="view === 'people'"
				:mode="mode"
				:people="tripReportModelData.people"
				:reportId="tripReportModelData.tripReportId"
				:currentUser="currentUser"
			/>
			<!-- Edit narrative -->
			<EditReportNarrative
				v-if="view === 'narrative'"
				:mode="mode"
				ref="editNarrative"
				:narrative="tripReportModelData.narrative"
				:reportId="tripReportModelData.tripReportId"
			/>
			<!-- Add comment -->
			<EditReportComment v-if="view === 'comment'" ref="editComment" :mode="mode" :reportId="tripReportModelData.tripReportId" />
		</template>

		<template v-slot:footer>
			<!-- Edit nav -->
			<nav
				id="edit-report-nav"
				class="Page-section Page-section--primaryDark"
				:class="view === 'narrative' || view === 'comment' ? 'nofade' : ''"
			>
				<div class="Page-section-inner Page-section-inner--md">
					<EditReportNav
						:view="view"
						:mode="mode"
						ref="editReportNav"
						:advanceable="advanceable"
						@clicked-retreat="goBack"
						@clicked-advance="saveStep"
					/>
				</div>
			</nav>
		</template>
	</CloFixedPanels>
</template>

<script>
import CloFixedPanels from '@/layouts/CloFixedPanels/CloFixedPanels.vue';
import ReportTitle from './components/ReportTitle.vue';
import EditReportNav from './components/EditReportNav.vue';
import EditReportDefinition from './components/EditReportDefinition.vue';
import EditReportChecklists from './components/EditReportChecklists.vue';
import EditReportComment from './components/EditReportComment.vue';
import EditReportNarrative from './components/EditReportNarrative.vue';
import EditReportPeople from './components/EditReportPeople.vue';

export default {
	name: 'EditTripReportApp',
	components: {
		CloFixedPanels,
		ReportTitle,
		EditReportComment,
		EditReportNarrative,
		EditReportNav,
		EditReportDefinition,
		EditReportChecklists,
		EditReportPeople,
	},
	data() {
		return {
			baseUrl: window.baseUrl,
			tripReportModelData: window.tripReport ? window.tripReport : '',
			onClickNext: '',
			advanceable: false,
			currentUser: null,
			isFirstVisit: false,
		};
	},
	computed: {
		mode() {
			return window.tripReport ? 'edit' : 'create';
		},
		view() {
			return window.viewReqParam ? window.viewReqParam : 'definition';
		},
		// get locale by reformating java string fromfor browswer
		locale() {
			let locale = 'en-US';
			// Check for global locale variable set in JSP
			if (window.locale) {
				locale = window.locale.replace('_', '-');
			}
			// console.log(`Locale: ${locale}`);
			return locale;
		},
	},
	created() {
		this.setCurrentUser();
	},
	mounted() {
		// if loading existing data, advance/next button enabled.
		if (this.mode === 'edit') {
			this.advanceable = true;
		}
		this.setIsFirstVisit();
	},
	methods: {
		// handle "back" click from footer nav component
		goBack() {
			if (this.view === 'checklists' && this.isFirstVisit && this.currentUser.role === 'owner') {
				window.location.href = `${baseUrl}tripreport/edit/${this.tripReportModelData.tripReportId}?view=definition`;
			} else {
				window.history.back();
			}
		},
		// handle "next" click from footer nav component
		saveStep() {
			if (this.view === 'definition') {
				this.$refs.editReportDef.submitDefinitionForm();
			} else if (this.view === 'checklists') {
				this.$refs.editChecklists.saveExcludedChecklists();
			} else if (this.view === 'narrative') {
				this.$refs.editNarrative.saveNarrative();
			} else if (this.view === 'comment') {
				this.$refs.editComment.saveComment();
			} else {
				window.location.href = `${baseUrl}tripreport/${this.tripReportModelData.tripReportId}`;
			}
		},
		advanceStep(emittedVal) {
			this.advanceable = emittedVal;
		},
		setCurrentUser() {
			// if editing, needed for personal dates
			if (this.tripReportModelData) {
				this.currentUser = {};
				this.currentUser.userId = window.userId;
				const tripReportPeople = this.tripReportModelData.people;
				const tripPeopleIds = tripReportPeople.map((person) => person.userId);
				if (tripPeopleIds.indexOf(this.currentUser.userId) !== -1) {
					const currentParticipant = tripReportPeople.filter((person) => {
						return person.userId === this.currentUser.userId;
					});
					this.currentUser.tripReportPersonId = currentParticipant[0].tripReportPersonId;
					this.currentUser.personBeginDt = currentParticipant[0].personBeginDt;
					this.currentUser.personEndDt = currentParticipant[0].personEndDt;
					this.currentUser.role = currentParticipant[0].role;
				}
			}
		},
		setIsFirstVisit() {
			const queryString = window.location.search;
			const urlParams = new URLSearchParams(queryString);
			const welcome = urlParams.get('welcome');
			if (welcome) {
				this.isFirstVisit = true;
			}
		},
	},
};
</script>

<style lang="scss">
#edit-report {
	.FixedPanels-main-sub {
		display: none !important; // hide sub panel UI
	}
}
#edit-report-nav {
	// grid-area: nav;
	position: relative;
	padding-bottom: $space-lg;
	&:not(.nofade) {
		&::before {
			position: absolute;
			content: '';
			height: $space-lg;
			width: 100%;
			left: 0;
			top: 0;
			transform: translateY(-100%);
			background: linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
		}
	}
}

#edit-report {
	.Button:disabled {
		cursor: not-allowed;
		font-weight: bold;
		color: $color-white;
		background-color: $color-neutral-3;
		&:hover,
		&:active,
		&:focus {
			color: $color-white;
			background-color: $color-neutral-3;
		}
	}
}
</style>
